import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const AttendanceForm = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        member_name: '',
        admission_number: '',
        nic_number: '',
        email: '',
        club_id: '',
        position: 'Member',
    });
    const [showEmailField, setShowEmailField] = useState(false);
    const [checkingUser, setCheckingUser] = useState(false);
    const [recognizedUser, setRecognizedUser] = useState(null);
    const [clubs, setClubs] = useState([]);

    const fetchProject = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/attendance.php?token=${token}`);
            const data = await response.json();

            if (data.success) {
                setProject(data.data.project);
                // Default club_id to the project hosting club
                setFormData(prev => ({ ...prev, club_id: data.data.project.club_id }));
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to load project details');
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchClubs = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/attendance.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_clubs' }),
            });
            const data = await response.json();
            if (data.success) {
                setClubs(data.data.clubs);
            }
        } catch (err) {
            console.error('Failed to fetch clubs');
        }
    }, []);

    useEffect(() => {
        fetchProject();
        fetchClubs();
    }, [fetchProject, fetchClubs]);

    const checkUserStatus = async (idValue) => {
        if (!idValue || idValue.length < 5) return;
        setCheckingUser(true);
        try {
            const response = await fetch(`${API_BASE_URL}/attendance.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'check_user',
                    id_value: idValue
                }),
            });
            const data = await response.json();
            if (data.success) {
                if (data.data.exists) {
                    setShowEmailField(false);
                    setRecognizedUser(data.data.user.name);
                    setFormData(prev => ({ 
                        ...prev, 
                        member_name: data.data.user.name,
                        club_id: data.data.user.club_id
                    }));
                    toast.success(`Identification Verified! Welcome back, ${data.data.user.name}`, { icon: '🤝' });
                } else {
                    setShowEmailField(true);
                    setRecognizedUser(null);
                    toast('New Member Detected. Please complete registration below.', { icon: '✨' });
                }
            }
        } catch (err) {
            // If check fails, show a notice but allow manual toggle
            toast('Could not verify ID automatically. If you are new, click "Register as New Member" below.', { icon: '⚠️', duration: 5000 });
        } finally {
            setCheckingUser(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Final safety check if they clicked submit too fast
        if (checkingUser) {
            toast.loading('Finalizing identification...', { duration: 2000 });
            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                action: 'mark_attendance',
                project_id: project.id,
                ...formData,
            };

            const response = await fetch(`${API_BASE_URL}/attendance.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (data.success) {
                toast.success(showEmailField ? 'Account Provisioned & Attendance Marked' : 'Attendance Successfully Logged');
                navigate('/attendance-success', {
                    state: {
                        attendance: data.data.attendance,
                        project: project,
                    },
                });
            } else {
                if (data.message.includes('Member not recognized') || data.message.includes('provide an email')) {
                    setShowEmailField(true);
                    toast.error('Member not found. Please complete the registration details below.', { duration: 5000 });
                } else {
                    toast.error(data.message || 'Verification Error');
                }
            }
        } catch (err) {
            toast.error('Tactical sync failure. Please check your connection.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 md:p-8 flex justify-center items-center md:p-12">
                    <div className="animate-spin rounded-full h-10 md:h-12 w-10 md:w-12 border-b-2 border-black"></div>
                </div>
            </div>
        );
    }

    if (error && !project) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white w-full max-w-md rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] overflow-hidden border border-slate-100 transform transition-all animate-premium">
                    <div className="bg-rose-500 p-8 text-center border-b border-rose-600/10">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-md">
                            <span className="text-3xl text-white">🚫</span>
                        </div>
                        <h1 className="text-xl font-black text-white uppercase tracking-tight">Access Restricted</h1>
                    </div>
                    <div className="p-10 text-center">
                        <div className="text-slate-900 font-black text-lg mb-2 uppercase tracking-tight">{error}</div>
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-10 leading-relaxed">
                            UNAUTHORIZED DEVICE OR EXPIRED TOKEN DETECTED
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            className="w-full bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest py-4 rounded-xl hover:bg-black transition-all shadow-lg shadow-slate-900/10"
                        >
                            Return to Secure Zone
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const inputClasses = "w-full px-5 py-3.5 rounded-xl border border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-normal";
    const labelClasses = "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1";

    return (
        <div className="min-h-screen bg-slate-50/50 flex items-center justify-center p-4 font-sans">
            <div className="bg-white w-full max-w-md rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] overflow-hidden md:max-w-lg border border-slate-100 animate-premium">
                <div className="bg-slate-900 p-8 text-center border-b border-white/5">
                    <h1 className="text-2xl font-black text-white mb-2 uppercase tracking-tight">Sync Attendance</h1>
                    <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] opacity-60">
                        Lion Identification Services
                    </div>
                </div>

                <div className="p-8 md:p-10">
                    <div className="bg-slate-50/50 rounded-2xl p-6 mb-8 border border-slate-100">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500 mb-2">Project Session</h3>
                        <h2 className="text-xl font-black text-slate-900 mb-1 leading-tight uppercase tracking-tight">{project.name}</h2>
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-4">{project.club_name}</p>
                        <div className="flex gap-2">
                            <span className="px-3 py-1 bg-white border border-slate-200 text-slate-400 rounded-lg text-[10px] font-black uppercase tracking-widest">
                                {project.club_category}
                            </span>
                            <span className="px-3 py-1 bg-white border border-slate-200 text-slate-400 rounded-lg text-[10px] font-black uppercase tracking-widest">
                                {project.club_type}
                            </span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">


                        <div className="relative">
                            <label htmlFor="member_name" className={labelClasses}>
                                Full Identification Name
                            </label>
                            <input
                                type="text"
                                id="member_name"
                                value={formData.member_name}
                                onChange={(e) => setFormData({ ...formData, member_name: e.target.value })}
                                required
                                className={`${inputClasses} ${recognizedUser ? 'bg-indigo-50 border-indigo-200' : ''}`}
                                placeholder="Enter your full name"
                                disabled={!!recognizedUser}
                            />
                            {recognizedUser && (
                                <div className="absolute right-4 top-[38px] flex items-center gap-2">
                                    <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Recognized</span>
                                    <span className="text-sm">✔️</span>
                                </div>
                            )}
                        </div>

                        {project.club_category === 'Alpha' ? (
                            <div>
                                <label htmlFor="admission_number" className={labelClasses}>
                                    Alpha Admission #
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="admission_number"
                                        value={formData.admission_number}
                                        onChange={(e) => setFormData({ ...formData, admission_number: e.target.value })}
                                        onBlur={(e) => checkUserStatus(e.target.value)}
                                        required
                                        className={`${inputClasses} ${checkingUser ? 'pr-12' : ''}`}
                                        placeholder="Required for Sync"
                                    />
                                    {checkingUser && (
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                            <div className="w-5 h-5 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label htmlFor="nic_number" className={labelClasses}>
                                    Omega NIC Number
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="nic_number"
                                        value={formData.nic_number}
                                        onChange={(e) => setFormData({ ...formData, nic_number: e.target.value })}
                                        onBlur={(e) => checkUserStatus(e.target.value)}
                                        required
                                        className={`${inputClasses} ${checkingUser ? 'pr-12' : ''}`}
                                        placeholder="Required for Sync"
                                    />
                                    {checkingUser && (
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                            <div className="w-5 h-5 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Manual toggle for new members if auto-detect fails */}
                        {!showEmailField && !recognizedUser && (
                            <button
                                type="button"
                                onClick={() => { setShowEmailField(true); setRecognizedUser(null); }}
                                className="w-full text-center text-[10px] font-black uppercase tracking-widest text-indigo-500 hover:text-indigo-700 py-2 transition-colors"
                            >
                                ✨ I am a New Member — Register Here
                            </button>
                        )}

                        {showEmailField && (
                            <div className="animate-premium space-y-6 pt-8 border-t border-slate-100">
                                <div className="bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100/50">
                                    <h3 className="text-indigo-900 font-black text-xs uppercase tracking-tight flex items-center gap-2 mb-2">
                                        <span className="text-lg">✨</span> Registration Required
                                    </h3>
                                    <p className="text-[10px] text-indigo-600/70 leading-relaxed font-bold uppercase tracking-widest">
                                        Identifier not recognized. Please provide secondary details to initialize your profile and record session attendance.
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="email" className={labelClasses}>
                                        System Access Email
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        className={inputClasses}
                                        placeholder="leo@domain.com"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="position" className={labelClasses}>
                                            Organizational Position
                                        </label>
                                        <select
                                            id="position"
                                            value={formData.position}
                                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                            required
                                            className={inputClasses}
                                        >
                                            <option value="Member">Member</option>
                                            <option value="President">President</option>
                                            <option value="Vice President">Vice President</option>
                                            <option value="Secretary">Secretary</option>
                                            <option value="Treasurer">Treasurer</option>
                                            <option value="Executive Member">Executive Member</option>
                                            <option value="Board Member">Board Member</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="club_id" className={labelClasses}>
                                            Tactical Unit (Club)
                                        </label>
                                        <select
                                            id="club_id"
                                            value={formData.club_id}
                                            onChange={(e) => setFormData({ ...formData, club_id: e.target.value })}
                                            required
                                            className={inputClasses}
                                        >
                                            <option value="">Select Your Club</option>
                                            {clubs.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className={`w-full font-black text-[10px] uppercase tracking-[0.2em] py-4 rounded-xl flex items-center justify-center gap-2 transition-all group disabled:opacity-50 shadow-xl hover:-translate-y-0.5 ${
                                showEmailField 
                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/10' 
                                : 'bg-slate-900 hover:bg-black text-white shadow-slate-900/10'
                            }`}
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    SYNCING...
                                </span>
                            ) : (
                                showEmailField ? 'Register & Mark Attendance' : 'Mark Attendance'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AttendanceForm;
